import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Loader2, Upload, Download, Trash2, CheckCircle, AlertTriangle, 
  DollarSign, Users, BookOpen, Calendar, FileSpreadsheet, Eye, UserPlus, ChevronsUpDown, Check,
} from 'lucide-react';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const EarningsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [earningsMonths, setEarningsMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthDetails, setMonthDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Import form state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importMonth, setImportMonth] = useState('');
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [monthToDelete, setMonthToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Unmatched-author resolver state
  const [writers, setWriters] = useState([]);
  const [resolvingName, setResolvingName] = useState(null);  // which unmatched name has picker open
  const [resolvePickerOpen, setResolvePickerOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  
  // Export state
  const [exporting, setExporting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportData, setExportData] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEarningsMonths();
    fetchSettings();
    fetchWriters();
  }, []);

  const fetchWriters = async () => {
    try {
      const r = await axios.get(`${API}/admin/users?role=writer`);
      const list = (r.data || []).filter(u => u.role === 'writer' && u.status === 'active');
      list.sort((a, b) => `${a.voornaam || ''} ${a.van || ''}`.localeCompare(
        `${b.voornaam || ''} ${b.van || ''}`, 'af', { sensitivity: 'base' }));
      setWriters(list);
    } catch (e) {
      console.warn('Could not load writers list for matching:', e?.message);
    }
  };

  const openResolvePicker = (unmatchedName) => {
    setResolvingName(unmatchedName);
    setResolvePickerOpen(true);
  };

  const handleResolveUnmatched = async (writerId) => {
    if (!monthDetails?.import || !resolvingName) return;
    const { month, year } = monthDetails.import;
    setResolving(true);
    try {
      const r = await axios.post(
        `${API}/admin/earnings/${month}/${year}/resolve-unmatched`,
        { unmatched_name: resolvingName, writer_id: writerId }
      );
      toast.success(r.data?.message || 'Gepaar.');
      setResolvePickerOpen(false);
      setResolvingName(null);
      // Refresh both the list of months (totals may change) and the current month detail
      await Promise.all([
        fetchEarningsMonths(),
        fetchMonthDetails(month, year),
      ]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie paar nie.');
    } finally {
      setResolving(false);
    }
  };

  const fetchEarningsMonths = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/earnings/months`);
      setEarningsMonths(response.data || []);
    } catch (error) {
      console.error('Failed to fetch earnings months:', error);
      toast.error('Kon nie verdienste data laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/earnings-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchMonthDetails = async (month, year) => {
    setLoadingDetails(true);
    setSelectedMonth({ month, year });
    try {
      const response = await axios.get(`${API}/admin/earnings/${month}/${year}`);
      setMonthDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch month details:', error);
      toast.error('Kon nie maand besonderhede laai nie');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleImport = async () => {
    if (!importFile || !importMonth || !importYear) {
      toast.error('Kies asseblief maand, jaar en lêer');
      return;
    }

    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('month', importMonth);
      formData.append('year', importYear.toString());

      const response = await axios.post(`${API}/admin/earnings/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success(response.data.message);
      fetchEarningsMonths();
      
      // Only close if no unmatched authors
      if (response.data.unmatched_count === 0) {
        setShowImportDialog(false);
        resetImportForm();
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Invoer het misluk';
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const resetImportForm = () => {
    setImportMonth('');
    setImportYear(new Date().getFullYear());
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!monthToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/earnings/${monthToDelete.month}/${monthToDelete.year}`);
      toast.success(`Verdienste vir ${monthToDelete.month} ${monthToDelete.year} verwyder`);
      setShowDeleteDialog(false);
      setMonthToDelete(null);
      setSelectedMonth(null);
      setMonthDetails(null);
      fetchEarningsMonths();
    } catch (error) {
      toast.error('Kon nie verwyder nie');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportFNB = async (month, year) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/earnings/${month}/${year}/fnb-export`);
      
      setExportData({
        csvContent: response.data.csv_content,
        filename: response.data.filename || `FNB_Payments_${month}_${year}.csv`,
        summary: response.data.summary || {},
        totalPayments: response.data.total_payments || 0,
        grandTotal: response.data.grand_total || 0,
        missingBankDetails: response.data.missing_bank_details || [],
        month,
        year
      });
      setShowExportDialog(true);
      
      fetchEarningsMonths();
    } catch (error) {
      toast.error('Kon nie FNB CSV genereer nie');
    } finally {
      setExporting(false);
    }
  };

  const triggerFNBDownload = () => {
    if (!exportData) return;
    const token = localStorage.getItem('token');
    // Same approach as the working ZIP export - window.open with direct backend URL
    window.open(`${API}/admin/earnings/${exportData.month}/${exportData.year}/fnb-download?token=${token}`, '_blank');
    setShowExportDialog(false);
  };

  const triggerRecipientsCsvDownload = () => {
    if (!exportData) return;
    const token = localStorage.getItem('token');
    window.open(`${API}/admin/earnings/${exportData.month}/${exportData.year}/fnb-recipients-csv?token=${token}`, '_blank');
    setShowExportDialog(false);
  };

  const triggerOnceOffCsvDownload = () => {
    if (!exportData) return;
    const token = localStorage.getItem('token');
    window.open(`${API}/admin/earnings/${exportData.month}/${exportData.year}/fnb-once-off-csv?token=${token}`, '_blank');
    setShowExportDialog(false);
  };

  const handleMarkPaid = async (month, year) => {
    try {
      await axios.post(`${API}/admin/earnings/${month}/${year}/mark-paid`);
      toast.success(`${month} ${year} gemerk as betaal`);
      fetchEarningsMonths();
      if (selectedMonth?.month === month && selectedMonth?.year === year) {
        fetchMonthDetails(month, year);
      }
    } catch (error) {
      toast.error('Kon nie merk as betaal nie');
    }
  };

  const formatCurrency = (amount) => {
    return `R${parseFloat(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Verdienste Bestuur</h1>
          <p className="text-gray-600">Invoer maandelikse verdienste en genereer FNB betalings</p>
        </div>
        <Button 
          onClick={() => setShowImportDialog(true)}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="import-earnings-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Invoer CSV
        </Button>
      </div>

      {/* Settings Summary */}
      {settings && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-gray-600">Munt Koers:</span>
                <span className="ml-2 font-semibold text-blue-700">R{settings.coin_to_rand_rate}</span>
              </div>
              <div>
                <span className="text-gray-600">Google/Apple:</span>
                <span className="ml-2 font-semibold">{settings.google_apple_percentage}%</span>
              </div>
              <div>
                <span className="text-gray-600">App:</span>
                <span className="ml-2 font-semibold text-orange-600">{settings.app_percentage}%</span>
              </div>
              <div>
                <span className="text-gray-600">Skrywers:</span>
                <span className="ml-2 font-semibold text-green-600">{settings.writer_percentage}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Months List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Ingevoerde Maande
              </CardTitle>
            </CardHeader>
            <CardContent>
              {earningsMonths.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Geen verdienste data ingevoer nie</p>
              ) : (
                <div className="space-y-2">
                  {earningsMonths.map((item) => (
                    <div
                      key={`${item.month}-${item.year}`}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedMonth?.month === item.month && selectedMonth?.year === item.year
                          ? 'bg-orange-50 border-orange-300'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() => fetchMonthDetails(item.month, item.year)}
                      data-testid={`month-item-${item.month}-${item.year}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{item.month} {item.year}</span>
                        <div className="flex gap-1">
                          {item.payment_completed && (
                            <CheckCircle className="h-4 w-4 text-green-500" title="Betaal" />
                          )}
                          {item.fnb_exported && !item.payment_completed && (
                            <FileSpreadsheet className="h-4 w-4 text-blue-500" title="Uitgevoer" />
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {item.entries_count} skrywers • {formatCurrency(item.writer_amount)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Month Details */}
        <div className="lg:col-span-2">
          {loadingDetails ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                </div>
              </CardContent>
            </Card>
          ) : monthDetails ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{selectedMonth.month} {selectedMonth.year}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportFNB(selectedMonth.month, selectedMonth.year)}
                      disabled={exporting}
                      data-testid="export-fnb-btn"
                    >
                      {exporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-1" />
                          FNB Betaling
                        </>
                      )}
                    </Button>
                    {!monthDetails.import?.payment_completed && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-300"
                        onClick={() => handleMarkPaid(selectedMonth.month, selectedMonth.year)}
                        data-testid="mark-paid-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Merk Betaal
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300"
                      onClick={() => {
                        setMonthToDelete(selectedMonth);
                        setShowDeleteDialog(true);
                      }}
                      data-testid="delete-month-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>
                  Ingevoer op {new Date(monthDetails.import?.import_date).toLocaleDateString('af-ZA')} deur {monthDetails.import?.imported_by}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Totals Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gray-800">{monthDetails.import?.total_coins?.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Totale Munte</div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-700">{formatCurrency(monthDetails.import?.total_rand)}</div>
                    <div className="text-sm text-blue-600">Totale Inkomste</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-orange-600">{formatCurrency(monthDetails.import?.app_amount)}</div>
                    <div className="text-sm text-orange-500">App Inkomste</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(monthDetails.import?.writer_amount)}</div>
                    <div className="text-sm text-green-500">Skrywer Uitbetalings</div>
                  </div>
                </div>

                {/* Status badges */}
                <div className="flex gap-2 flex-wrap">
                  {monthDetails.import?.payment_completed && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" /> Betaal op {new Date(monthDetails.import?.payment_completed_date).toLocaleDateString('af-ZA')}
                    </span>
                  )}
                  {monthDetails.import?.fnb_exported && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      <FileSpreadsheet className="h-4 w-4" /> FNB Uitgevoer
                    </span>
                  )}
                  {monthDetails.import?.unmatched_authors?.length > 0 && (
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" /> {monthDetails.import?.unmatched_authors?.length} Ongepaarde Skrywers
                    </span>
                  )}
                </div>

                {/* Unmatched Authors Warning */}
                {monthDetails.import?.unmatched_authors?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg" data-testid="unmatched-authors-panel">
                    <h4 className="font-semibold text-yellow-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Ongepaarde Skrywers
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Hierdie skrywers kon nie outomaties in die stelsel gevind word nie. Klik &ldquo;Pas met skrywer&rdquo; om die regte skrywer te kies.
                    </p>
                    <ul className="mt-3 space-y-2">
                      {monthDetails.import?.unmatched_authors.map((author, idx) => (
                        <li
                          key={`unmatched-${idx}-${author.name}`}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white rounded border border-yellow-200 p-2"
                          data-testid={`unmatched-row-${idx}`}
                        >
                          <div className="text-sm text-gray-700 min-w-0">
                            <strong>{author.name}</strong>
                            <span className="text-gray-500"> — {author.coins} munte</span>
                            {author.books?.length > 0 && (
                              <div className="text-xs text-gray-500 italic mt-0.5 truncate">
                                {author.books.join(' · ')}
                              </div>
                            )}
                          </div>
                          <Popover
                            open={resolvePickerOpen && resolvingName === author.name}
                            onOpenChange={(open) => {
                              if (!open) { setResolvePickerOpen(false); setResolvingName(null); }
                            }}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-500 text-blue-700 hover:bg-blue-50 flex-shrink-0"
                                onClick={() => openResolvePicker(author.name)}
                                disabled={resolving}
                                data-testid={`unmatched-pick-btn-${idx}`}
                              >
                                <UserPlus className="h-3 w-3 mr-1" />
                                Pas met skrywer
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-[320px]" align="end">
                              <Command
                                filter={(value, search) => {
                                  if (!search) return 1;
                                  const v = value.toLowerCase();
                                  const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
                                  return tokens.every(t => v.includes(t)) ? 1 : 0;
                                }}
                              >
                                <CommandInput
                                  placeholder={`Soek skrywer vir "${author.name}"...`}
                                  data-testid="unmatched-search-input"
                                  autoFocus
                                />
                                <CommandList>
                                  <CommandEmpty>Geen skrywer pas nie.</CommandEmpty>
                                  <CommandGroup>
                                    {writers.map(w => {
                                      const display = `${w.voornaam || ''} ${w.van || ''}`.trim();
                                      const filterValue = `${display} ${w.email || ''}`.toLowerCase();
                                      return (
                                        <CommandItem
                                          key={w.id}
                                          value={filterValue}
                                          onSelect={() => handleResolveUnmatched(w.id)}
                                          disabled={resolving}
                                          data-testid={`unmatched-option-${w.id}`}
                                        >
                                          <Check className="mr-2 h-4 w-4 opacity-0" />
                                          <div className="flex flex-col min-w-0">
                                            <span className="truncate">{display}</span>
                                            <span className="text-xs text-gray-500 truncate">{w.email}</span>
                                          </div>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Writer Entries Table */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Skrywer Verdienste ({monthDetails.entries?.length || 0})
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left p-3 font-medium">Skrywer</th>
                            <th className="text-left p-3 font-medium">Boeke</th>
                            <th className="text-right p-3 font-medium">Munte</th>
                            <th className="text-right p-3 font-medium">Uitbetaling</th>
                            <th className="text-center p-3 font-medium">Bank</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {monthDetails.entries?.map((entry) => (
                            <tr key={entry.id} className="hover:bg-gray-50">
                              <td className="p-3">
                                <div className="font-medium">{entry.writer_name}</div>
                                <div className="text-xs text-gray-500">{entry.writer_email}</div>
                              </td>
                              <td className="p-3">
                                <div className="text-xs text-gray-600 max-w-xs truncate" title={entry.books?.join(', ')}>
                                  {entry.books?.join(', ')}
                                </div>
                              </td>
                              <td className="p-3 text-right">{entry.coins_spent?.toLocaleString()}</td>
                              <td className="p-3 text-right font-medium text-green-600">
                                {formatCurrency(entry.writer_payout)}
                              </td>
                              <td className="p-3 text-center">
                                {entry.has_bank_details ? (
                                  <CheckCircle className="h-4 w-4 text-green-500 inline" />
                                ) : (
                                  <AlertTriangle className="h-4 w-4 text-yellow-500 inline" title="Geen bankbesonderhede" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Kies 'n maand om besonderhede te sien</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Invoer Verdienste CSV
            </DialogTitle>
            <DialogDescription>
              Laai die maandelikse verdienste CSV lêer op
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Maand</Label>
                <Select value={importMonth} onValueChange={setImportMonth}>
                  <SelectTrigger data-testid="import-month-select">
                    <SelectValue placeholder="Kies maand" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jaar</Label>
                <Input
                  type="number"
                  value={importYear}
                  onChange={(e) => setImportYear(parseInt(e.target.value))}
                  min={2020}
                  max={2030}
                  data-testid="import-year-input"
                />
              </div>
            </div>

            <div>
              <Label>CSV Lêer</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Verwagte kolomme: Author, Books, Coins Spent, Purchases, Rentals
              </p>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.unmatched_count > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'} border`}>
                <div className="font-medium mb-2">
                  {importResult.unmatched_count > 0 ? (
                    <span className="text-yellow-700">Ingevoer met waarskuwings</span>
                  ) : (
                    <span className="text-green-700">Suksesvol ingevoer</span>
                  )}
                </div>
                <div className="text-sm space-y-1">
                  <p>Skrywers verwerk: {importResult.total_entries}</p>
                  <p>Totale munte: {importResult.totals?.coins?.toLocaleString()}</p>
                  <p>Skrywer uitbetalings: {formatCurrency(importResult.totals?.writers)}</p>
                </div>
                {importResult.unmatched_count > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-yellow-700">
                      {importResult.unmatched_count} skrywers kon nie gepaar word nie:
                    </p>
                    <ul className="text-sm text-gray-600 mt-1">
                      {importResult.unmatched_authors?.map((a, i) => (
                        <li key={i}>{a.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  resetImportForm();
                }}
              >
                Kanselleer
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing || !importFile || !importMonth}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="confirm-import-btn"
              >
                {importing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Invoer...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />Invoer</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Verwyder Verdienste Data</DialogTitle>
            <DialogDescription>
              Is jy seker jy wil die verdienste data vir {monthToDelete?.month} {monthToDelete?.year} verwyder?
              Hierdie aksie kan nie ongedaan gemaak word nie.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Kanselleer
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verwyder...</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" />Verwyder</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FNB Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              FNB Betaling - {exportData?.month} {exportData?.year}
            </DialogTitle>
            <DialogDescription>
              Die FNB betalings lêer is gereed om af te laai
            </DialogDescription>
          </DialogHeader>

          {exportData && (
            <div className="space-y-4">
              {/* Payment Summary */}
              <div className="grid grid-cols-2 gap-3">
                {exportData.summary?.writers?.count > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm text-green-700 font-medium">Skrywers</div>
                    <div className="text-lg font-bold text-green-800">{exportData.summary.writers.count}</div>
                    <div className="text-xs text-green-600">{formatCurrency(exportData.summary.writers.total)}</div>
                  </div>
                )}
                {exportData.summary?.proofreaders?.count > 0 && (
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="text-sm text-purple-700 font-medium">Proeflesers</div>
                    <div className="text-lg font-bold text-purple-800">{exportData.summary.proofreaders.count}</div>
                    <div className="text-xs text-purple-600">{formatCurrency(exportData.summary.proofreaders.total)}</div>
                  </div>
                )}
                {exportData.summary?.translators?.count > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-blue-700 font-medium">Vertalers</div>
                    <div className="text-lg font-bold text-blue-800">{exportData.summary.translators.count}</div>
                    <div className="text-xs text-blue-600">{formatCurrency(exportData.summary.translators.total)}</div>
                  </div>
                )}
                {exportData.summary?.handlers?.count > 0 && (
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-sm text-orange-700 font-medium">Hanteerders</div>
                    <div className="text-lg font-bold text-orange-800">{exportData.summary.handlers.count}</div>
                    <div className="text-xs text-orange-600">{formatCurrency(exportData.summary.handlers.total)}</div>
                  </div>
                )}
              </div>

              {/* Grand Total */}
              <div className="bg-gray-100 p-4 rounded-lg flex justify-between items-center">
                <span className="font-medium text-gray-700">Totale Betalings: {exportData.totalPayments}</span>
                <span className="text-xl font-bold text-gray-900">{formatCurrency(exportData.grandTotal)}</span>
              </div>

              {/* Missing Bank Details Warning */}
              {exportData.missingBankDetails?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <h4 className="font-medium text-yellow-800 flex items-center gap-1 text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    Ontbrekende Bankbesonderhede ({exportData.missingBankDetails.length})
                  </h4>
                  <ul className="mt-2 space-y-1">
                    {exportData.missingBankDetails.map((person, idx) => (
                      <li key={`missing-${person.email || person.name}-${idx}`} className="text-xs text-yellow-700">
                        {person.name} ({person.type}) - {formatCurrency(person.amount)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Download Button */}
              <div className="flex flex-col gap-3 pt-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1">
                  <p className="font-semibold">Hoe om by FNB op te laai:</p>
                  <p>1. FNB → Payments → <strong>Once Off</strong> tab → <strong>+Add</strong> → <strong>Import</strong></p>
                  <p>2. Kies File Format = <strong>“csv”</strong>, laai die aflaaide lêer op</p>
                  <p>3. Bevestig — bedrae en verwysings is reeds in die lêer</p>
                  <p className="pt-1 text-[11px] text-amber-800">Wenk: Moenie die lêer in Excel oopmaak en weer stoor nie — dit strip die voorloop-nulle van rekeningnommers.</p>
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white py-3"
                  data-testid="download-fnb-recipients-csv-btn"
                  onClick={triggerRecipientsCsvDownload}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Laai FNB Betalings CSV Af
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowExportDialog(false);
                    setExportData(null);
                  }}
                  data-testid="close-export-dialog-btn"
                >
                  Sluit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EarningsManagement;
